import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Col, Form, Input, Radio, Row } from "antd";
import {
  PATTERNS,
  STATUS_TYPE,
  TWO_FACTOR_AUTH,
  WORKSPACE_USER_TYPE,
} from "constants/appConstant";
import parsePhoneNumber from "libphonenumber-js";

import AppButton from "components/button";

import AppSelect from "../../../components/inputs/select";
import { USER_TYPE } from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import { doUpdateUserDetail } from "../../../stores/slices/user";
import {
  doCreateWorkspaceUser,
  doUpdateWorkspaceUser,
} from "../../../stores/slices/workspaceUser";

const UserDetailForm = props => {
  const {
    isViewFrm,
    workspaceInfo,
    onCancel,
    itemSelected,
    onSuccess,
    onUpdate,
  } = props;
  const formRef = React.useRef(null);
  const authInfo = useSelector(AuthInfo);

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isView, setIsView] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const twoFAOptions = [
    {
      value: TWO_FACTOR_AUTH.NONE,
      label: t("text.none"),
    },
    {
      value: TWO_FACTOR_AUTH.GOOGLE_AUTH,
      label: t("text.googleAuthenticator"),
    },
    {
      value: TWO_FACTOR_AUTH.EMAIL,
      label: t("text.email"),
    },
  ];
  const statusOptions = [
    {
      value: STATUS_TYPE.ACTIVE,
      label: t("status.verified"),
    },
    {
      value: STATUS_TYPE.PENDING,
      label: t("status.pending"),
    },
  ];

  const dispatch = useDispatch();

  const onFinish = async fieldsValue => {
    const params = {
      workspaceId: workspaceId,
      firstName: fieldsValue["firstName"]?.trim() || "",
      lastName: fieldsValue["lastName"]?.trim() || "",
      type: fieldsValue["userType"],
      phoneNumber: fieldsValue["phoneNumber"]?.trim(),
      twoFactorAuth: fieldsValue["twoFA"],
      status: fieldsValue["accountStatus"],
    };

    setLoading(true);

    if (itemSelected) {
      if (itemSelected.workspace && itemSelected.workspace.workspaceUser) {
        params.userId = itemSelected.id;

        const { payload } = await dispatch(doUpdateWorkspaceUser(params));
        if (payload && payload.status === 200) {
          setLoading(false);
          onSuccess();
        } else {
          setLoading(false);
        }
      } else {
        const updateParams = {
          id: itemSelected.id,
          customer: {
            firstName: params.firstName,
            lastName: params.lastName,
          },
          contact: {
            phoneNumber: params.phoneNumber,
          },
        };
        const { payload } = await dispatch(doUpdateUserDetail(updateParams));
        if (payload && payload.status === 200) {
          setLoading(false);
          onSuccess();
        } else {
          setLoading(false);
        }
      }
    } else {
      params.email = fieldsValue["email"];
      const { payload } = await dispatch(doCreateWorkspaceUser(params));
      if (payload && payload.status === 200) {
        setLoading(false);
        onSuccess();
      } else {
        setLoading(false);
      }
    }
  };

  const validateMessages = {
    required: t("validate.required"),
    types: {
      email: t("validate.email.invalid"),
    },
  };

  const initValue = itemSelected => {
    // console.log("itemSelected", itemSelected);
    let value;
    if (itemSelected) {
      value = {
        userType: itemSelected.type,
        email: itemSelected.email || itemSelected.contact?.email,
      };
      if (itemSelected) {
        value.firstName = itemSelected.firstName;
        value.lastName = itemSelected.lastName;
      }
      if (itemSelected.contact) {
        value.phoneNumber = itemSelected.contact.phoneNumber;
      }
      if (itemSelected.workspace) {
        value.workspaceName = itemSelected.workspace?.name;
        value.userType = itemSelected.workspace?.workspaceUser?.type;
        value.twoFA = itemSelected.workspace?.workspaceUser?.twoFactorAuth;
        value.accountStatus = itemSelected.workspace?.workspaceUser?.status;
      }
      if (!value.twoFA) {
        value.twoFA = TWO_FACTOR_AUTH.NONE;
      }
    } else {
      value = {
        twoFA: TWO_FACTOR_AUTH.NONE,
        accountStatus: STATUS_TYPE.ACTIVE,
      };
      if (workspaceId) {
        value.userType = USER_TYPE.SUPER_ADMIN;
      }
    }

    if (value.accountStatus === STATUS_TYPE.INACTIVE) {
      statusOptions.push({
        value: "INACTIVE",
        label: t("status.inactive"),
        disabled: true,
      });
    }

    if (value.accountStatus === STATUS_TYPE.DELETE) {
      statusOptions.push({
        value: "DELETE",
        label: t("status.deleted"),
        disabled: true,
      });
    }
    return value;
  };

  useEffect(() => {
    setIsView(isViewFrm);
    if (itemSelected && itemSelected.workspace) {
      setWorkspaceId(itemSelected.workspace?.id);
    }
  }, [isViewFrm]);

  const checkValidPhoneNumber = phone => {
    let phoneNumber = phone;
    if (
      PATTERNS.PHONE_NUMBER_LENGTH.test(phone) &&
      PATTERNS.ZERO_ONLY.test(phone)
    ) {
      const validPhoneNumber = parsePhoneNumber(phoneNumber);
      if (validPhoneNumber && validPhoneNumber.isPossible()) {
        if (validPhoneNumber.country && validPhoneNumber.isValid()) {
          const newValue = validPhoneNumber.formatInternational();
          return newValue.replaceAll(" ", "").replaceAll("+", "");
        }
      } else {
        phoneNumber = phone.includes("+") ? phone : `+${phoneNumber}`;
        const validContactNumber = parsePhoneNumber(phoneNumber);
        if (validContactNumber && validContactNumber.isPossible()) {
          if (validContactNumber.country && validContactNumber.isValid()) {
            const newValue = validContactNumber.formatInternational();
            return newValue.replaceAll(" ", "").replaceAll("+", "");
          } else {
            return false;
          }
        } else {
          const validPhoneNumber2 = parsePhoneNumber(phone, "AU");
          if (validPhoneNumber2 && validPhoneNumber2.isPossible()) {
            if (validPhoneNumber2.country && validPhoneNumber2.isValid()) {
              const newValue = validPhoneNumber2.formatInternational();
              return newValue.replaceAll(" ", "").replaceAll("+", "");
            } else {
              return false;
            }
          } else {
            const validContactNumber2 = parsePhoneNumber(phoneNumber, "AU");
            if (validContactNumber2 && validContactNumber2.isPossible()) {
              if (
                validContactNumber2.country &&
                validContactNumber2.isValid()
              ) {
                const newValue = validContactNumber2.formatInternational();
                return newValue.replaceAll(" ", "").replaceAll("+", "");
              } else {
                return false;
              }
            }
          }
        }
      }
    } else {
      return false;
    }
  };

  return (
    <div style={{ height: "100%" }}>
      <Form
        ref={formRef}
        initialValues={initValue(itemSelected)}
        layout={"vertical"}
        name="nest-messages"
        onFinish={onFinish}
        validateMessages={validateMessages}
      >
        <>
          {itemSelected.workspace?.workspaceUser && (
            <>
              {authInfo.type === USER_TYPE.SUPER_ADMIN && (
                <>
                  <p style={frmStyle.labelTitle}>{t("user.workspaceName")}</p>
                  <Form.Item
                    name="workspaceName"
                    rules={[
                      { required: true },
                      {
                        pattern: new RegExp(PATTERNS.XSS_PATTERN),
                        message: t("validate.noxss"),
                      },
                    ]}
                  >
                    <Input disabled placeholder={t("user.workspaceName")} />
                  </Form.Item>
                </>
              )}

              <p style={frmStyle.labelTitle}>{t("user.userType")}</p>
              <Form.Item name="userType">
                <Radio.Group disabled={!workspaceId || isView}>
                  <Radio value="ADMIN" style={frmStyle.lineHeightLg}>
                    {t("role.admin")}
                  </Radio>
                  <Radio value="OPERATOR" style={frmStyle.lineHeightLg}>
                    {t("role.operator")}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}

          <Row gutter="16">
            <Col span={12}>
              <p style={frmStyle.labelTitle}>{t("user.firstName")}</p>
              <Form.Item
                name="firstName"
                rules={[
                  { type: "string" },
                  {
                    pattern: new RegExp(PATTERNS.XSS_PATTERN),
                    message: t("validate.noxss"),
                  },
                ]}
              >
                <Input
                  disabled={isView}
                  maxLength={255}
                  placeholder={t("user.firstName")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <p style={frmStyle.labelTitle}>{t("user.lastName")}</p>
              <Form.Item
                name="lastName"
                rules={[
                  { type: "string" },
                  {
                    pattern: new RegExp(PATTERNS.XSS_PATTERN),
                    message: t("validate.noxss"),
                  },
                ]}
              >
                <Input
                  disabled={isView}
                  maxLength={255}
                  placeholder={t("user.lastName")}
                />
              </Form.Item>
            </Col>
          </Row>

          <p style={frmStyle.labelTitle}>{t("user.userEmail")}</p>
          <Form.Item name="email" rules={[{ required: true, type: "email" }]}>
            <Input disabled placeholder={t("user.userEmail")} />
          </Form.Item>

          <p style={frmStyle.labelTitle}>{t("user.phoneNumber")}</p>
          <Form.Item
            name="phoneNumber"
            rules={[
              { type: "string" },
              {
                pattern: new RegExp(PATTERNS.PHONE_NUMBER),
                message: t("validate.phoneNumber.invalid"),
              },
              ({ setFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.resolve();
                  } else {
                    const isValid = checkValidPhoneNumber(value);
                    if (isValid) {
                      setFieldValue("number", isValid);
                      return Promise.resolve();
                    } else {
                      return Promise.reject(
                        new Error(`${t("validate.phoneNumber.invalid")}`)
                      );
                    }
                  }
                },
              }),
            ]}
          >
            <Input disabled={isView} placeholder={t("text.userPhoneNumber")} />
          </Form.Item>

          {itemSelected.workspace?.workspaceUser && (
            <>
              <p style={frmStyle.labelTitle}>{t("user.twoFA")}</p>
              <Form.Item name="twoFA" rules={[{ required: true }]}>
                <AppSelect disabled={isView} options={twoFAOptions}></AppSelect>
              </Form.Item>

              <p style={frmStyle.labelTitle}>{t("user.accountStatus")}</p>
              <Form.Item name="accountStatus" rules={[{ required: true }]}>
                <AppSelect
                  disabled={isView}
                  options={statusOptions}
                ></AppSelect>
              </Form.Item>
            </>
          )}
        </>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            gap: "16px",
          }}
        >
          <AppButton
            label={t("button.cancel")}
            color={"grey"}
            loading={loading}
            htmlType="button"
            onClick={() => {
              onCancel();
            }}
          />
          {isView &&
            (authInfo.type === USER_TYPE.SUPER_ADMIN ||
              !workspaceInfo ||
              workspaceInfo.workspaceUser?.type ===
                WORKSPACE_USER_TYPE.ADMIN) &&
            itemSelected?.workspace?.workspaceUser?.status !==
              STATUS_TYPE.INACTIVE && (
              <AppButton
                label={t("button.edit")}
                color={"blue"}
                loading={loading}
                type="primary"
                htmlType="button"
                disabled={
                  authInfo.id === itemSelected.id ||
                  itemSelected?.workspace?.status === STATUS_TYPE.DELETE ||
                  (authInfo.type !== USER_TYPE.SUPER_ADMIN &&
                    workspaceInfo?.workspaceUser?.type !==
                      WORKSPACE_USER_TYPE.ADMIN) ||
                  (itemSelected.workspace?.workspaceUser?.status !==
                    STATUS_TYPE.ACTIVE &&
                    itemSelected.workspace?.workspaceUser?.status !==
                      STATUS_TYPE.PENDING)
                }
                onClick={() => {
                  onUpdate();
                }}
              />
            )}
          {!isView && (
            <AppButton
              label={itemSelected ? t("button.save") : t("button.invite")}
              color={"blue"}
              loading={loading}
              type="primary"
              htmlType="submit"
            />
          )}
        </div>
      </Form>
    </div>
  );
};

const frmStyle = {
  labelTitle: {
    marginBottom: "6px",
  },
  fontWeightBold: {
    fontWeight: "bold",
  },
  lineHeightLg: {
    lineHeight: "1.2rem",
  },
  appearBox: {
    backgroundColor: "#F9FAFB",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "16px",
  },
};

export default UserDetailForm;
